import { makeAutoObservable, runInAction, computed } from './mobx-config.js';
import { storeEvents, EVENTS } from './storeEvents.js';

/**
 * Store centralisé pour le journal des opérations et le suivi des tâches de fond.
 *
 * Deux types d'entrées (kind) :
 * - 'pipeline' : étapes GEDCOM séquentielles (log instantané, pas de progress bar)
 * - 'task'     : tâches de fond async (géocodage, AI notes — avec progress bar)
 */

/** Labels pour les étapes du pipeline GEDCOM */
const PIPELINE_STEP_LABELS = {
    file_reading:  'Lecture du fichier GEDCOM',
    file_parsing:  'Analyse de la structure GEDCOM',
    geocoding:     'Préparation des données géographiques',
    data_building: 'Organisation des données généalogiques',
    finalization:  'Finalisation et sélection racine'
};

class BackgroundTaskStore {
    /** @type {Map<string, TaskEntry>} */
    tasks = new Map();

    /** Le journal a été activé (au moins un PROCESS.START reçu) */
    sessionStarted = false;

    /** Délai avant suppression d'une tâche de fond terminée (ms) */
    static TASK_CLEANUP_DELAY = 300_000; // 5 minutes

    constructor() {
        makeAutoObservable(this, {
            activeCount: computed,
            allTasks: computed,
            hasEntries: computed
        });
        this.setupEventListeners();
    }

    // ── Public API ──────────────────────────────────────────

    /**
     * Enregistre une entrée de journal instantanée (pipeline step).
     * Créée directement comme 'completed' — pas de cycle running→completed.
     */
    logEntry(id, label, message = '') {
        runInAction(() => {
            const now = Date.now();
            this.tasks.set(id, {
                id,
                label,
                kind: 'pipeline',
                status: 'completed',
                progress: null,
                message,
                startTime: now,
                endTime: now
            });
        });
        // Pas d'auto-cleanup pour les entrées pipeline
    }

    /**
     * Enregistre une tâche de fond async (avec progress bar).
     */
    registerTask(id, label) {
        runInAction(() => {
            this.tasks.set(id, {
                id,
                label,
                kind: 'task',
                status: 'running',
                progress: null,
                message: '',
                startTime: Date.now(),
                endTime: null
            });
        });
    }

    updateProgress(id, percentage, message = '') {
        const task = this.tasks.get(id);
        if (!task) return;
        runInAction(() => {
            task.progress = percentage;
            task.message = message;
        });
    }

    completeTask(id, summary = '') {
        const task = this.tasks.get(id);
        if (!task) return;
        runInAction(() => {
            task.status = 'completed';
            task.progress = 100;
            task.message = summary;
            task.endTime = Date.now();
        });
        this._scheduleCleanup(id);
    }

    failTask(id, error = '') {
        const task = this.tasks.get(id);
        if (!task) return;
        runInAction(() => {
            task.status = 'failed';
            task.message = typeof error === 'string' ? error : (error?.message || 'Erreur inconnue');
            task.endTime = Date.now();
        });
        this._scheduleCleanup(id);
    }

    /**
     * Efface toutes les entrées non-running du journal.
     */
    clearJournal() {
        runInAction(() => {
            for (const [id, task] of this.tasks.entries()) {
                if (task.status !== 'running') {
                    this.tasks.delete(id);
                }
            }
        });
    }

    // ── Computed ────────────────────────────────────────────

    get activeCount() {
        let count = 0;
        for (const task of this.tasks.values()) {
            if (task.status === 'running') count++;
        }
        return count;
    }

    get allTasks() {
        return Array.from(this.tasks.values());
    }

    get hasEntries() {
        return this.tasks.size > 0;
    }

    // ── Event subscriptions ────────────────────────────────

    setupEventListeners() {
        // ── Pipeline GEDCOM steps ──
        storeEvents.subscribe(EVENTS.PROCESS.START, (data) => {
            if (!this.sessionStarted) {
                runInAction(() => { this.sessionStarted = true; });
            }
            const label = PIPELINE_STEP_LABELS[data.step] || data.message || data.step;
            this.logEntry(`pipeline-${data.step}`, label);
        });

        storeEvents.subscribe(EVENTS.PROCESS.COMPLETE, (data) => {
            const parts = [];
            if (data.individualsCount) {
                parts.push(`${data.individualsCount.toLocaleString()} personnes`);
            }
            if (data.performanceSummary?.totalDuration) {
                parts.push(`en ${data.performanceSummary.totalDuration}`);
            }
            this.logEntry('pipeline-complete', 'Import terminé', parts.join(' '));
        });

        storeEvents.subscribe(EVENTS.PROCESS.ERROR, (data) => {
            runInAction(() => {
                const now = Date.now();
                this.tasks.set('pipeline-error', {
                    id: 'pipeline-error',
                    label: 'Erreur de traitement',
                    kind: 'pipeline',
                    status: 'failed',
                    progress: null,
                    message: data.error || 'Erreur inconnue',
                    startTime: now,
                    endTime: now
                });
            });
        });

        // ── Background task: AI Notes extraction ──
        storeEvents.subscribe(EVENTS.AI_NOTES.PROCESSING_STARTED, () => {
            this.registerTask('ai-notes', 'Analyse AI des notes');
        });

        storeEvents.subscribe(EVENTS.AI_NOTES.PROCESSING_PROGRESS, (data) => {
            this.updateProgress(
                'ai-notes',
                data.percentage,
                `${data.processed} / ${data.total} notes`
            );
        });

        storeEvents.subscribe(EVENTS.AI_NOTES.PROCESSING_COMPLETED, (data) => {
            const totalEvents = data.totalEvents || data.total_events_extracted || 0;
            this.completeTask(
                'ai-notes',
                `${totalEvents} événement${totalEvents > 1 ? 's' : ''} extrait${totalEvents > 1 ? 's' : ''}`
            );
        });

        storeEvents.subscribe(EVENTS.AI_NOTES.PROCESSING_FAILED, (data) => {
            this.failTask('ai-notes', data.error);
        });

        // ── Background task: Geolocation ──
        storeEvents.subscribe(EVENTS.TOWN.UPDATE_START, () => {
            this.registerTask('geocoding', 'Géolocalisation des villes');
        });

        storeEvents.subscribe(EVENTS.TOWN.UPDATE_COMPLETE, () => {
            this.completeTask('geocoding', 'Coordonnées mises à jour');
        });

        storeEvents.subscribe(EVENTS.TOWN.UPDATE_ERROR, (error) => {
            this.failTask('geocoding', error);
        });

        // ── Background task: Historical events loading ──
        storeEvents.subscribe(EVENTS.HISTORICAL_EVENTS.LOAD_START, () => {
            this.registerTask('historical-events', 'Contexte historique');
        });

        storeEvents.subscribe(EVENTS.HISTORICAL_EVENTS.LOAD_COMPLETE, (data) => {
            this.completeTask(
                'historical-events',
                `${data.count} événement${data.count > 1 ? 's' : ''} historique${data.count > 1 ? 's' : ''}`
            );
        });

        storeEvents.subscribe(EVENTS.HISTORICAL_EVENTS.LOAD_ERROR, (data) => {
            this.failTask('historical-events', data.error);
        });
    }

    // ── Private ────────────────────────────────────────────

    _scheduleCleanup(id) {
        const task = this.tasks.get(id);
        if (!task || task.kind === 'pipeline') return; // Pas de cleanup pour les entrées pipeline

        setTimeout(() => {
            runInAction(() => {
                const t = this.tasks.get(id);
                if (t && t.status !== 'running') {
                    this.tasks.delete(id);
                }
            });
        }, BackgroundTaskStore.TASK_CLEANUP_DELAY);
    }
}

const backgroundTaskStore = new BackgroundTaskStore();
export default backgroundTaskStore;
